/**
 * Menu registry for the Permission Access Layer (SA-1592 Phase A0).
 *
 * Maps stable, refactor-proof MENU keys to their route metadata. Endpoints are
 * sourced from the nine `findMenuAccess('/...')` calls in `src/app/router.jsx`
 * (L181-189) plus the FE-static routes that have no BE menu entry yet.
 *
 * `aliasOf` mirrors `ROUTE_ACCESS_ALIASES` in `authUtils.js`: a sub-flow route
 * with no menu entry of its own resolves its access against the owning menu.
 *
 * `feStatic` marks routes injected on the FE (no BE menu gate today) — see
 * SUPER_ADMIN_STATIC_MENUS / STATIC_FE_MENUS notes in authUtils.js.
 *
 * This file is PURE metadata — no React, no localStorage.
 */

export const MENU = {
  ANALYTICS: 'ANALYTICS',
  POLICY_OVERVIEW: 'POLICY_OVERVIEW',
  INSURANCE_PORTAL: 'INSURANCE_PORTAL',
  BUKU_TARIF: 'BUKU_TARIF',
  ROLE_MANAGEMENT: 'ROLE_MANAGEMENT',
  USER_MANAGEMENT: 'USER_MANAGEMENT',
  COMPANY_MANAGEMENT: 'COMPANY_MANAGEMENT',
  BUKU_TARIF_LOG: 'BUKU_TARIF_LOG',
  INSURANCE_CLAIM_LOG: 'INSURANCE_CLAIM_LOG',
  QUALITY_CHECKER: 'QUALITY_CHECKER',
  POLICY_MANAGEMENT: 'POLICY_MANAGEMENT',
  CLAIM_CONFIGURATION: 'CLAIM_CONFIGURATION',
  QC_ANALYTICS_DASHBOARD: 'QC_ANALYTICS_DASHBOARD',
};

export const MENU_REGISTRY = {
  [MENU.ANALYTICS]: {
    endpoint: '/analytics-dashboard',
    labelKey: 'menu_analytics_dashboard',
  },
  [MENU.POLICY_OVERVIEW]: {
    endpoint: '/policy-overview',
    labelKey: 'menu_policy_overview',
  },
  [MENU.INSURANCE_PORTAL]: {
    endpoint: '/insurance-portal',
    labelKey: 'menu_insurance_portal',
  },
  [MENU.BUKU_TARIF]: {
    endpoint: '/buku-tarif',
    labelKey: 'menu_buku_tarif',
  },
  [MENU.ROLE_MANAGEMENT]: {
    endpoint: '/management-role',
    labelKey: 'menu_role_management',
  },
  [MENU.USER_MANAGEMENT]: {
    endpoint: '/management-user',
    labelKey: 'menu_user_management',
  },
  // FE-static for super-admin until BE provisions the menu (SA-1501).
  [MENU.COMPANY_MANAGEMENT]: {
    endpoint: '/management-company',
    labelKey: 'menu_company_management',
    feStatic: true,
  },
  [MENU.BUKU_TARIF_LOG]: {
    endpoint: '/buku-tarif-log',
    labelKey: 'menu_buku_tarif_log',
  },
  [MENU.INSURANCE_CLAIM_LOG]: {
    endpoint: '/insurance-claim-log',
    labelKey: 'menu_insurance_claim_log',
  },
  // FE-static — injected into the sidebar from the FE (SA-1552); no BE menu gate.
  // `slug` resolves role-level access against the BE menu (id 59) whose
  // `frontend_endpoint` is empty and therefore cannot be matched by path.
  [MENU.QUALITY_CHECKER]: {
    endpoint: '/quality-checker',
    labelKey: 'menu_quality_checker',
    slug: 'company-insurance-claim-qc',
    feStatic: true,
  },
  // Sub-flow route reached from the company detail page; resolves access against
  // COMPANY_MANAGEMENT, mirroring ROUTE_ACCESS_ALIASES in authUtils.js.
  [MENU.POLICY_MANAGEMENT]: {
    endpoint: '/policy-management',
    aliasOf: MENU.COMPANY_MANAGEMENT,
  },
  // SA-1862 — super-admin "Konfigurasi Klaim" area, also reached from the
  // company detail page. Same alias treatment as Policy Management; the
  // matching entry in ROUTE_ACCESS_ALIASES (authUtils.js) must stay in sync.
  [MENU.CLAIM_CONFIGURATION]: {
    endpoint: '/claim-configuration',
    aliasOf: MENU.COMPANY_MANAGEMENT,
  },
  // QC Dashboard Analytics (SA-2118) — dedicated BE menu "Insurance Claim QC
  // Analytic". The BE guards every `/insurance-claim-analytics/qc/*` route
  // with this slug, so access is matched by `slug` first and by the
  // `frontend_endpoint` second — never by the auto-increment DB id, and never
  // through the Analytics menu. The company's QC_CLAIM feature gate is applied
  // separately (FeatureGate in QcDashboardPage + sidebar filter).
  // `accessFallback`: until the BE seeds this menu into every role's `menus`,
  // access falls back to the Quality Checker menu — the BE grants QC Analytic
  // to QC roles at runtime (QC depends on it), so a QC user's API calls pass.
  // The real menu always wins when present.
  [MENU.QC_ANALYTICS_DASHBOARD]: {
    endpoint: '/qc-analytics-dashboard',
    labelKey: 'app_qc_analytics_dashboard',
    slug: 'company-insurance-claim-qc-analytic',
    accessFallback: MENU.QUALITY_CHECKER,
  },
};

// Reverse lookup: endpoint string → MENU key. Built once at module load.
export const ENDPOINT_TO_KEY = Object.keys(MENU_REGISTRY).reduce((acc, key) => {
  const entry = MENU_REGISTRY[key];
  if (entry?.endpoint) acc[entry.endpoint] = key;
  return acc;
}, {});
